import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { scrollTop } from '@share/utils';
import { Routes } from '@share/constants';

import './style.scss';

interface RouteParams {
  accountUsername: string;
}

const CampingsBookingHowToPage: React.FC = () => {
  useEffect(() => {
    scrollTop();
  }, []);
  const history = useHistory();
  const params = useParams<RouteParams>();
  const { accountUsername } = params;

  const navigateToWelcome = () => {
    history.push(`/${accountUsername}${Routes.CampingsWelcome}`);
  };

  const navigateToFAQs = () => {
    history.push(`/${accountUsername}${Routes.CampingsFAQs}`);
  };

  const navigateToCampings = () => {
    history.push(`/${accountUsername}${Routes.Campings}`);
  };

  // Icon Components
  const ArrowLeftIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"/>
      <polyline points="12 19 5 12 12 5"/>
    </svg>
  );

  const MonitorIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
      <line x1="8" y1="21" x2="16" y2="21"/>
      <line x1="12" y1="17" x2="12" y2="21"/>
    </svg>
  );

  const PhoneIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  );

  const CheckCircleIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  );

  const AlertCircleIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  );

  const webBookingSteps = [
    {
      number: 1,
      title: 'Log in to AORUCN.com',
    },
    {
      number: 2,
      title: 'Choose your membership level',
      description: 'Select Gold (camping) or Platinum (all benefits)',
    },
    {
      number: 3,
      title: 'Find your campground',
      description: 'Filter by State/Region → open a Campground Card',
    },
    {
      number: 4,
      title: 'Review the details',
      description: 'Check Deal, Deal Rules, Map, Amenities, and Promo Code',
    },
    {
      number: 5,
      title: 'Book at the campground website',
      description: 'Click "Book at Campground" to open their site',
    },
    {
      number: 6,
      title: 'Apply your promo code',
      description: 'Select dates/site type, then paste the promo code in the site\'s promo/discount field',
    },
    {
      number: 7,
      title: 'Complete checkout',
      description: 'Finish the booking and save your confirmation',
    },
  ];

  const phoneBookingSteps = [
    {
      number: 1,
      title: 'Follow steps 1-4 above',
      description: 'Log in and find your campground with the promo code',
    },
    {
      number: 2,
      title: 'Call the campground',
      description: 'Use the phone number shown on the campground card',
    },
    {
      number: 3,
      title: 'Mention AOR/UCN and your promo code',
      description: 'The campground staff will apply your member discount',
    },
    {
      number: 4,
      title: 'Confirm details',
      description: 'Verify the rate, rules, and cancellation policy verbally or by email',
    },
  ];

  const tips = [
    'Check blackout dates, minimum nights, and site type eligibility before booking',
    'Codes are case-sensitive on some sites—copy/paste from your dashboard for accuracy',
    'If the discount doesn\'t appear at payment screen, stop and re-check the rules, dates, and code before you submit',
  ];

  return (
    <div className="campings-booking-how-to-page">
      <div className="campings-booking-how-to">
        <div className="container">
          <div className="campings-booking-how-to__content">
            {/* Back Button */}
            <button className="campings-booking-how-to__back-button" onClick={navigateToWelcome}>
              <ArrowLeftIcon />
              Back to Home
            </button>

            {/* Page Title */}
            <div className="campings-booking-how-to__header">
              <h1 className="campings-booking-how-to__title">Booking How-To</h1>
              <p className="campings-booking-how-to__subtitle">Step-by-step guide to booking your campground stay</p>
            </div>

            {/* Web Booking Section */}
            <div className="campings-booking-how-to__card">
              <div className="campings-booking-how-to__card-header">
                <div className="campings-booking-how-to__card-icon">
                  <MonitorIcon />
                </div>
                <div>
                  <h2 className="campings-booking-how-to__card-title">Web-Booking Campground</h2>
                  <p className="campings-booking-how-to__card-subtitle">Most campgrounds support online booking</p>
                </div>
              </div>

              <div className="campings-booking-how-to__steps">
                {webBookingSteps.map((step) => (
                  <div key={step.number} className="campings-booking-how-to__step">
                    <div className="campings-booking-how-to__step-number">{step.number}</div>
                    <div>
                      <p className="campings-booking-how-to__step-title">{step.title}</p>
                      {step.description && (
                        <p className="campings-booking-how-to__step-description">{step.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Phone Booking Section */}
            <div className="campings-booking-how-to__card">
              <div className="campings-booking-how-to__card-header">
                <div className="campings-booking-how-to__card-icon">
                  <PhoneIcon />
                </div>
                <div>
                  <h2 className="campings-booking-how-to__card-title">Phone-Booking Campground</h2>
                  <p className="campings-booking-how-to__card-subtitle">For campgrounds without online booking</p>
                </div>
              </div>

              <div className="campings-booking-how-to__steps">
                {phoneBookingSteps.map((step) => (
                  <div key={step.number} className="campings-booking-how-to__step">
                    <div className="campings-booking-how-to__step-number">{step.number}</div>
                    <div>
                      <p className="campings-booking-how-to__step-title">{step.title}</p>
                      {step.description && (
                        <p className="campings-booking-how-to__step-description">{step.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tips for Success */}
            <div className="campings-booking-how-to__card campings-booking-how-to__card--tips">
              <div className="campings-booking-how-to__tips-header">
                <CheckCircleIcon />
                <h2 className="campings-booking-how-to__tips-title">Tips for Success</h2>
              </div>
              <ul className="campings-booking-how-to__tips-list">
                {tips.map((tip, index) => (
                  <li key={index} className="campings-booking-how-to__tip-item">
                    <span className="campings-booking-how-to__tip-bullet">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Help Section */}
            <div className="campings-booking-how-to__help">
              <AlertCircleIcon />
              <h3 className="campings-booking-how-to__help-title">Need Help?</h3>
              <p className="campings-booking-how-to__help-text">
                If you're having trouble with your booking, check our FAQs or contact support.
              </p>
              <div className="campings-booking-how-to__help-buttons">
                <button 
                  className="campings-booking-how-to__button campings-booking-how-to__button--primary"
                  onClick={navigateToCampings}
                >
                  Browse Campgrounds
                </button>
                <button 
                  className="campings-booking-how-to__button campings-booking-how-to__button--secondary"
                  onClick={navigateToFAQs}
                >
                  View FAQs
                </button>
                <button 
                  className="campings-booking-how-to__button campings-booking-how-to__button--outline"
                  onClick={navigateToWelcome}
                >
                  Back to Home
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="campings-booking-how-to__footer">
          <div className="container">
            <div className="campings-booking-how-to__footer-content">
              <p className="campings-booking-how-to__footer-text">
                &copy; 2025 AOR/UCN by RSI Vacations. All rights reserved.
              </p>
              <div className="campings-booking-how-to__footer-links">
                <button onClick={navigateToFAQs} className="campings-booking-how-to__footer-link">FAQs</button>
                <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="campings-booking-how-to__footer-link">Booking Guide</button>
                <a href="#" className="campings-booking-how-to__footer-link">Privacy</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export { CampingsBookingHowToPage };
